import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { VersionBarChart } from './charts/VersionBarChart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteData } from '../types';

interface VersionStat {
  version: string;
  activeSites: number;
  connectedSites: number;
  offlineSites: number;
}

interface VersionDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: VersionStat[];
  siteData: SiteData[];
  latestVersion?: string;
}

export const VersionDataModal = ({ isOpen, onClose, data, siteData, latestVersion }: VersionDataModalProps) => {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [selectedWRegion, setSelectedWRegion] = useState<string>('all');
  const [selectedSubRegion, setSelectedSubRegion] = useState<string>('all');

  const handleVersionClick = (version: string) => {
    setSelectedVersion(version);
  };

  const handleBackToChart = () => {
    setSelectedVersion(null);
  };

  const handleClose = () => {
    setSelectedVersion(null);
    setSelectedWRegion('all');
    setSelectedSubRegion('all');
    onClose();
  };

  const handleWRegionChange = (value: string) => {
    setSelectedWRegion(value);
    setSelectedSubRegion('all'); // Reset subregion when changing main region
  };

  // Get unique regions for filter - use country/region columns
  const availableWRegions = useMemo(() => {
    const regionSet = new Set<string>();
    
    siteData.forEach(site => {
      if (site.country === 'United States') {
        regionSet.add('North America');
      } else if (site.region) {
        regionSet.add(site.region);
      }
    });
    
    return Array.from(regionSet)
      .map(region => ({ value: region, label: region }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [siteData]);

  // Get subregions (only for North America/US) - these are the region values for US sites
  const availableSubRegions = selectedWRegion === 'North America' 
    ? [...new Set(siteData
        .filter(site => site.country === 'United States')
        .map(site => site.region)
        .filter(region => region && region.trim() !== ''))]
    : [];

  // Filter data by region and subregion using country/region columns, then sort by version DESC
  const regionFilteredData = (selectedWRegion === 'all' ? data : data.map(versionStat => {
    const regionSites = siteData.filter(site => {
      // Use country/region logic for filtering
      let matchesRegion = false;
      
      if (selectedWRegion === 'North America') {
        matchesRegion = site.country === 'United States';
      } else {
        matchesRegion = site.region === selectedWRegion;
      }
      
      const matchesSubRegion = selectedWRegion !== 'North America' || selectedSubRegion === 'all' || site.region === selectedSubRegion;
      return matchesRegion && matchesSubRegion && site.mlVersion === versionStat.version;
    });
    
    return {
      ...versionStat,
      activeSites: regionSites.filter(site => site.activeUsers >= 3).length,
      connectedSites: regionSites.filter(site => site.isOnline && site.activeUsers < 3).length,
      offlineSites: regionSites.filter(site => !site.isOnline).length,
    };
  }).filter(versionStat => versionStat.activeSites > 0 || versionStat.connectedSites > 0))
    .sort((a, b) => {
      // Parse version numbers for proper comparison (e.g., "2.1.0" vs "10.0.0")
      const parseVersion = (version: string) => {
        const parts = version.split('.').map(part => parseInt(part.replace(/[^\d]/g, '')) || 0);
        return parts[0] * 10000 + (parts[1] || 0) * 100 + (parts[2] || 0);
      };
      return parseVersion(b.version) - parseVersion(a.version);
    });

  const filteredSites = selectedVersion 
    ? siteData.filter(site => {
        const matchesVersion = site.mlVersion === selectedVersion;
        
        // Use country/region logic for filtering
        let matchesRegion = selectedWRegion === 'all';
        if (!matchesRegion) {
          if (selectedWRegion === 'North America') {
            matchesRegion = site.country === 'United States';
          } else {
            matchesRegion = site.region === selectedWRegion;
          }
        }
        
        const matchesSubRegion = selectedWRegion !== 'North America' || selectedSubRegion === 'all' || site.region === selectedSubRegion;
        return matchesVersion && matchesRegion && matchesSubRegion;
      })
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-bold flex items-center gap-3 min-h-[2.5rem]">
            {selectedVersion && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBackToChart}
                className="p-1 h-8 w-8 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <span className="break-words">
              {selectedVersion ? `Sites using Version ${selectedVersion}` : 'MentorLearn Cloud Versions Data'}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        {selectedVersion ? (
          <div className="mt-6">
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Showing {filteredSites.length} sites using version {selectedVersion}
                {selectedWRegion !== 'all' && ` in ${availableWRegions.find(wr => wr.value === selectedWRegion)?.label}`}
                {selectedWRegion === 'US' && selectedSubRegion !== 'all' && ` (${selectedSubRegion})`}
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Site Name</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Site Type</TableHead>
                    <TableHead>Install Date</TableHead>
                    <TableHead>Active Users</TableHead>
                    <TableHead>12-Month Sessions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSites.map((site, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{site.siteName}</TableCell>
                      <TableCell>{site.customer}</TableCell>
                      <TableCell>{site.country}</TableCell>
                        <TableCell>
                          <Badge variant={
                            site.activeUsers >= 3 
                              ? "default" 
                              : site.isOnline 
                                ? "secondary" 
                                : "outline"
                          }
                          className={
                            site.activeUsers >= 3 
                              ? "" 
                              : site.isOnline 
                                ? "" 
                                : "border-red-300 text-red-600 bg-red-50"
                          }>
                            {site.activeUsers >= 3 
                              ? 'Active' 
                              : site.isOnline 
                                ? 'Connected' 
                                : 'Offline'}
                          </Badge>
                        </TableCell>
                        <TableCell>{site.siteType}</TableCell>
                        <TableCell>{site.siteInstallDate}</TableCell>
                        <TableCell>{site.activeUsers}</TableCell>
                        <TableCell>{site.numSessionsOnServerLast12Month}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <div className="mb-4 flex items-center gap-4 flex-wrap">
              <label htmlFor="wregion-filter" className="text-sm font-medium">Filter by Region:</label>
              <Select value={selectedWRegion} onValueChange={handleWRegionChange}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {availableWRegions.map(wRegion => (
                    <SelectItem key={wRegion.value} value={wRegion.value}>{wRegion.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedWRegion === 'North America' && availableSubRegions.length > 0 && (
                <>
                  <label htmlFor="subregion-filter" className="text-sm font-medium">Subregion:</label>
                  <Select value={selectedSubRegion} onValueChange={setSelectedSubRegion}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select subregion" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Subregions</SelectItem>
                      {availableSubRegions.map(subRegion => (
                        <SelectItem key={subRegion} value={subRegion}>{subRegion}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
            <VersionBarChart data={regionFilteredData} onVersionClick={handleVersionClick} latestVersion={latestVersion} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};